import type { ImageProps, ImageSizing, Slide } from '@features/powerpoint';
import { chartOptionsToBase64Svg } from '@features/powerpoint';
import { contentSlideHeaderV2 } from '../../../../../../utils/slide-header-v2';
import { themeV2 } from '../../../../../../domain/theme-v2';
import { DEFAULT_FONT } from '../../../../../../utils/constants-v2';
import { annotationsTextContent } from '../../../../../../slides/shared/annotations-text';
import {
  buildAgeChart,
  buildEducationChart,
  buildGenderChart,
  buildHomeownershipChart,
  buildMaritalStatusChart,
  buildChildrenPresenceChart,
  buildPetsChart,
  getChartColor,
  buildIncomeChart,
} from '@configurations/campaignAnalytics/demographicProfileChart';
import type { CampaignAnalyticsSlidesV2Props } from '../build-campaign-analytics-slides-v2';

const SMALL_CHART_WIDTH = 269;
const CHART_HEIGHT = 186;

const WIDTH = 2.81;
const HEIGHT = 1.94;

const POSITIONS: ImageSizing[] = [
  { type: 'contain', w: WIDTH, h: HEIGHT, x: 0.5, y: 1.81 },
  { type: 'contain', w: WIDTH, h: HEIGHT, x: 3.62, y: 1.81 },
  { type: 'contain', w: WIDTH, h: HEIGHT, x: 6.73, y: 1.81 },
  { type: 'contain', w: WIDTH, h: HEIGHT, x: 9.85, y: 1.81 },
  { type: 'contain', w: WIDTH, h: HEIGHT, x: 0.5, y: 3.7 },
  { type: 'contain', w: WIDTH, h: HEIGHT, x: 3.62, y: 3.7 },
  { type: 'contain', w: WIDTH, h: HEIGHT, x: 6.73, y: 3.7 },
  { type: 'contain', w: WIDTH, h: HEIGHT, x: 9.85, y: 3.7 },
];

export const demographicProfileChartSlideV2 = (
  props: CampaignAnalyticsSlidesV2Props
): Slide => {
  const chartBuilders = [
    {
      buildChart: buildGenderChart,
      stats:
        props.campaignAnalyticsReport.demographicProfile.data.visited
          .statsByGender,
    },
    {
      buildChart: buildMaritalStatusChart,
      stats:
        props.campaignAnalyticsReport.demographicProfile.data.visited
          .statsByMaritalStatus,
    },
    {
      buildChart: buildChildrenPresenceChart,
      stats:
        props.campaignAnalyticsReport.demographicProfile.data.visited
          .statsByChildrenPresence,
    },
    {
      buildChart: buildHomeownershipChart,
      stats:
        props.campaignAnalyticsReport.demographicProfile.data.visited
          .statsByHomeownership,
    },
    {
      buildChart: buildEducationChart,
      stats:
        props.campaignAnalyticsReport.demographicProfile.data.visited
          .statsByEducation,
    },
    {
      buildChart: buildPetsChart,
      stats:
        props.campaignAnalyticsReport.demographicProfile.data.visited
          .statsByPets,
    },
    {
      buildChart: buildAgeChart,
      stats:
        props.campaignAnalyticsReport.demographicProfile.data.visited
          .statsByAge,
    },
    {
      buildChart: buildIncomeChart,
      stats:
        props.campaignAnalyticsReport.demographicProfile.data.visited
          .statsByIncome,
    },
  ].filter(
    chart =>
      chart.stats.length > 0 && !chart.stats.every(item => item.value === 0)
  );

  const chartSvgs: ImageProps[] = chartBuilders.map((chartBuilder, index) => {
    const color = getChartColor(index);
    return {
      data: chartOptionsToBase64Svg({
        chartOptions: chartBuilder.buildChart(color)(
          chartBuilder.stats,
          true,
          SMALL_CHART_WIDTH,
          CHART_HEIGHT
        ),
        width: SMALL_CHART_WIDTH,
        height: CHART_HEIGHT,
      }),
      ...POSITIONS[index],
    };
  });

  return {
    slideMaster: props.slideMaster,
    texts: [
      contentSlideHeaderV2('Demographic Profile'),
      {
        text: 'Exposed Visitors (Saw the Ad and Visited)',
        options: {
          x: 5.03,
          y: 1.44,
          h: 0.47,
          w: 3.26,
          fontSize: 10,
          bold: true,
          color: themeV2.colors.metricBlue,
          fontFace: DEFAULT_FONT,
        },
      },
      ...annotationsTextContent(
        props.campaignAnalyticsReport.demographicProfile.takeaways.map(
          takeaway => takeaway.content
        ),
        props.campaignAnalyticsReport.demographicProfile.recommendations?.map(
          recommendation => recommendation.content
        )
      ),
    ],
    images: chartSvgs,
  };
};
